import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import { campaign, campaignAsset } from "../config/campaign";
import { coverGeometry, type LayoutKey, type Rect, type Transform } from "../lib/geometry";
import { UploadIcon } from "./Icons";

export type CanvasEditorHandle = {
  exportBlob: () => Promise<Blob>;
};

type Props = {
  layout: LayoutKey;
  image: HTMLImageElement | null;
  onChoose: () => void;
  onTransformChange: (transform: Transform) => void;
  transform: Transform;
  onFrameError: () => void;
};

type Point = { x: number; y: number };

export const CanvasEditor = forwardRef<CanvasEditorHandle, Props>(function CanvasEditor(
  { layout, image, onChoose, transform, onTransformChange, onFrameError },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<HTMLImageElement | null>(null);
  const pointers = useRef(new Map<number, Point>());
  const gesture = useRef({ distance: 0, zoom: 1, last: null as Point | null });
  const [frameReady, setFrameReady] = useState(false);
  const spec = useMemo(() => {
    const configured = campaign.layouts[layout];
    const [x, y, width, height] = configured.photoRect;
    const photoRect: Rect = { x, y, width, height };
    return { ...configured, photoRect };
  }, [layout]);

  useEffect(() => {
    setFrameReady(false);
    const frame = new Image();
    frame.onload = () => {
      frameRef.current = frame;
      setFrameReady(true);
    };
    frame.onerror = onFrameError;
    frame.src = campaignAsset(layout === "story" ? campaign.assets.storyFrame : campaign.assets.avatarFrame);
    return () => {
      frame.onload = null;
      frame.onerror = null;
    };
  }, [layout, onFrameError]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    canvas.width = spec.width;
    canvas.height = spec.height;
    ctx.clearRect(0, 0, spec.width, spec.height);

    const background = ctx.createLinearGradient(0, 0, spec.width, spec.height);
    background.addColorStop(0, campaign.theme.surfaceRaised);
    background.addColorStop(1, campaign.theme.deep);
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, spec.width, spec.height);

    if (image) {
      const geometry = coverGeometry(image.width, image.height, spec.photoRect, transform);
      ctx.save();
      if (layout === "avatar" && campaign.layouts.avatar.clipPhoto !== false) {
        const [circleX, circleY, circleRadius] = campaign.layouts.avatar.clipCircle;
        ctx.beginPath();
        ctx.arc(circleX, circleY, circleRadius, 0, Math.PI * 2);
        ctx.clip();
      } else {
        ctx.beginPath();
        ctx.rect(spec.photoRect.x, spec.photoRect.y, spec.photoRect.width, spec.photoRect.height);
        ctx.clip();
      }
      ctx.drawImage(image, geometry.x, geometry.y, geometry.width, geometry.height);
      ctx.restore();
    } else {
      ctx.fillStyle = campaign.theme.surface;
      if (layout === "avatar") {
        const [circleX, circleY, circleRadius] = campaign.layouts.avatar.clipCircle;
        ctx.beginPath();
        ctx.arc(circleX, circleY, circleRadius, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.fillRect(spec.photoRect.x, spec.photoRect.y, spec.photoRect.width, spec.photoRect.height);
      }
    }

    if (frameReady && frameRef.current) {
      ctx.drawImage(frameRef.current, 0, 0, spec.width, spec.height);
    }
  }, [frameReady, image, layout, spec, transform]);

  useImperativeHandle(ref, () => ({
    exportBlob: () =>
      new Promise<Blob>((resolve, reject) => {
        const canvas = canvasRef.current;
        if (!canvas) return reject(new Error("Canvas indisponível"));
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Não foi possível gerar a imagem"));
        }, "image/png");
      }),
  }));

  function canvasDelta(dx: number, dy: number) {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: dx * (spec.width / rect.width), y: dy * (spec.height / rect.height) };
  }

  function onPointerDown(event: PointerEvent<HTMLCanvasElement>) {
    if (!image) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...pointers.current.values()];
    if (points.length === 1) gesture.current.last = points[0];
    if (points.length === 2) {
      gesture.current.distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
      gesture.current.zoom = transform.zoom;
    }
  }

  function onPointerMove(event: PointerEvent<HTMLCanvasElement>) {
    if (!image || !pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...pointers.current.values()];

    if (points.length === 2) {
      const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
      const zoom = Math.min(3, Math.max(1, gesture.current.zoom * (distance / gesture.current.distance)));
      onTransformChange({ ...transform, zoom });
      return;
    }

    const last = gesture.current.last;
    if (!last) return;
    const delta = canvasDelta(event.clientX - last.x, event.clientY - last.y);
    onTransformChange({ ...transform, panX: transform.panX + delta.x, panY: transform.panY + delta.y });
    gesture.current.last = { x: event.clientX, y: event.clientY };
  }

  function onPointerEnd(event: PointerEvent<HTMLCanvasElement>) {
    pointers.current.delete(event.pointerId);
    const remaining = [...pointers.current.values()];
    gesture.current.last = remaining[0] ?? null;
    if (remaining.length === 1) gesture.current.zoom = transform.zoom;
  }

  function onKeyDown(event: KeyboardEvent<HTMLCanvasElement>) {
    if (!image) return;
    const amount = event.shiftKey ? 36 : 12;
    const moves: Partial<Record<string, Point>> = {
      ArrowLeft: { x: -amount, y: 0 },
      ArrowRight: { x: amount, y: 0 },
      ArrowUp: { x: 0, y: -amount },
      ArrowDown: { x: 0, y: amount },
    };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    onTransformChange({ ...transform, panX: transform.panX + move.x, panY: transform.panY + move.y });
  }

  return (
    <div className={`canvas-shell canvas-shell--${layout} ${image ? "has-photo" : ""}`}>
      <canvas
        ref={canvasRef}
        aria-label={image ? "Prévia do seu santinho. Arraste para reposicionar a foto." : "Prévia vazia do santinho"}
        className="editor-canvas"
        tabIndex={image ? 0 : -1}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onKeyDown={onKeyDown}
      />
      {!image && (
        <button className="upload-overlay" type="button" onClick={onChoose}>
          <span className="upload-mark"><UploadIcon /></span>
          <strong>Enviar minha foto</strong>
          <small>JPG, PNG ou WEBP</small>
        </button>
      )}
    </div>
  );
});
